// ----------------------------------------------------------------------

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
  HQ: '/hq',
  CLIENT: '/client',
  ERROR: '/error',
};

// ----------------------------------------------------------------------

/**
 * 全站路径定义（趣享租后台）
 *
 * 所有路径必须经此对象访问，禁止字符串硬编码。
 * legacy 子树仅供尚未删除的智慧社区 sections 编译，无对应路由。
 */
export const paths = {
  auth: {
    amplify: {
      resetPassword: `${ROOTS.AUTH}/amplify/reset-password`,
      updatePassword: `${ROOTS.AUTH}/amplify/update-password`,
    },
    jwt: {
      signIn: `${ROOTS.AUTH}/jwt/sign-in`,
      signUp: `${ROOTS.AUTH}/jwt/sign-up`,
    },
  },
  client: {
    root: `${ROOTS.CLIENT}`,
    resetPassword: `${ROOTS.CLIENT}/reset-password`,
    resetPasswordSuccess: `${ROOTS.CLIENT}/reset-password/success`,
    changePhone: `${ROOTS.CLIENT}/change-phone`,
    changePhoneSuccess: `${ROOTS.CLIENT}/change-phone/success`,
    feedback: `${ROOTS.CLIENT}/feedback`,
    help: `${ROOTS.CLIENT}/help`,
  },
  /** 租户后台 */
  dashboard: {
    root: ROOTS.DASHBOARD,
    home: `${ROOTS.DASHBOARD}/home`,
    /** 财务管理 */
    finance: {
      root: `${ROOTS.DASHBOARD}/finance`,
      /** 财务概览 */
      overview: `${ROOTS.DASHBOARD}/finance/overview`,
      /** 收款流水 */
      payments: `${ROOTS.DASHBOARD}/finance/payments`,
      /** 租金账单 */
      bills: `${ROOTS.DASHBOARD}/finance/bills`,
      /** 退款记录 */
      refunds: `${ROOTS.DASHBOARD}/finance/refunds`,
      /** 资金结算（结算单 + 提现记录） */
      settlements: `${ROOTS.DASHBOARD}/finance/settlements`,
      /** @deprecated 已合并至 settlements，保留兼容旧链接 */
      withdrawals: `${ROOTS.DASHBOARD}/finance/withdrawals`,
      /** 押金管理 */
      deposits: `${ROOTS.DASHBOARD}/finance/deposits`,
      /** 分账参与方 */
      participants: `${ROOTS.DASHBOARD}/finance/participants`,
      /** 清分规则 */
      clearingRules: `${ROOTS.DASHBOARD}/finance/clearing-rules`,
      /** @deprecated 清分记录已移至 HQ，保留兼容旧链接 */
      clearingRecords: `${ROOTS.DASHBOARD}/finance/clearing-records`,
    },
    /** 会员管理 */
    members: {
      root: `${ROOTS.DASHBOARD}/members`,
      /** 会员列表 */
      list: `${ROOTS.DASHBOARD}/members/list`,
    },

    /**业务管理 */
    business: {
      root: `${ROOTS.DASHBOARD}/business`,
      /** 资产管理 */
      assets: {
        /** 资产列表 */
        root: `${ROOTS.DASHBOARD}/business/assets`,
        /** 资产列表 */
        list: `${ROOTS.DASHBOARD}/business/assets/list`,
        /** 资产类目 */
        categories: `${ROOTS.DASHBOARD}/business/assets/category`,
        /** 调拨记录 */
        transfers: `${ROOTS.DASHBOARD}/business/assets/transfers`,
        /** 租用记录 */
        rentals: `${ROOTS.DASHBOARD}/business/assets/rentals`,
        /** 删除记录 */
        deletedRecords: `${ROOTS.DASHBOARD}/business/assets/deleted-records`,
      },
      /** 订单管理 */
      orders: {
        root: `${ROOTS.DASHBOARD}/business/orders`,
        /** 租赁订单 */
        rent: `${ROOTS.DASHBOARD}/business/orders/rent`,
      },
      /** 逾期管理 */
      overdue: `${ROOTS.DASHBOARD}/business/overdue`,
      /** 商品管理 */
      products: {
        root: `${ROOTS.DASHBOARD}/business/products`,
        /** 商品列表 */
        list: `${ROOTS.DASHBOARD}/business/products/list`,
        /** 商品类目 */
        category: `${ROOTS.DASHBOARD}/business/products/category`,
        /** 创建商品 */
        create: `${ROOTS.DASHBOARD}/business/products/create`,
        /** 编辑商品 */
        edit: (id: string) => `${ROOTS.DASHBOARD}/business/products/edit/${id}`,
      },
      /** 门店管理 */
      stores: {
        root: `${ROOTS.DASHBOARD}/business/stores`,
        /** 门店列表 */
        list: `${ROOTS.DASHBOARD}/business/stores/list`,
      },
    },
    /** 组织管理 */
    organization: {
      root: `${ROOTS.DASHBOARD}/organization`,
      /** 员工管理 */
      users: `${ROOTS.DASHBOARD}/organization/users`,
      /** 角色管理 */
      roles: `${ROOTS.DASHBOARD}/organization/roles`,
    },

    /** 系统管理 */
    system: {
      root: `${ROOTS.DASHBOARD}/system`,
      /** 系统设置 */
      settings: `${ROOTS.DASHBOARD}/system/settings`,
      /** 个人中心 */
      profile: `${ROOTS.DASHBOARD}/system/profile`,
    },
  },
  /** 平台超管（HQ）专属路由 */
  hq: {
    root: `${ROOTS.HQ}`,
    /** 概览 */
    overview: `${ROOTS.HQ}/overview`,
    /** 城市管理（HqCity） */
    cities: `${ROOTS.HQ}/cities`,
    /** 财务中心 */
    finance: {
      root: `${ROOTS.HQ}/finance`,
      /** 财务概览 */
      overview: `${ROOTS.HQ}/finance/overview`,
      /** 支付流水 */
      payments: `${ROOTS.HQ}/finance/payments`,
      /** 清分记录 */
      clearingRecords: `${ROOTS.HQ}/finance/clearing-records`,
      /** 结算单 */
      settlements: `${ROOTS.HQ}/finance/settlements`,
      /** 提现/打款 */
      withdrawals: `${ROOTS.HQ}/finance/withdrawals`,
      /** 退款管理 */
      refunds: `${ROOTS.HQ}/finance/refunds`,
      /** 清分规则 */
      clearingRules: `${ROOTS.HQ}/finance/clearing-rules`,
      /** 分账参与方 */
      participants: `${ROOTS.HQ}/finance/participants`,
      /** 对账中心 */
      reconciliation: `${ROOTS.HQ}/finance/reconciliation`,
    },
    /** 总部后台登录账号（isAdmin + 平台 ACL 角色，待独立列表 API） */
    adminUsers: `${ROOTS.HQ}/admin-users`,
    /** 租户管理 */
    tenant: {
      /** 租户列表 */
      list: `${ROOTS.HQ}/tenant/list`,
      /** 入驻申请审核 */
      enterpriseVerification: `${ROOTS.HQ}/tenant/enterprise-verification`,
    },
    /** C 端 App 注册用户 */
    appUser: {
      /** 用户列表 */
      list: `${ROOTS.HQ}/app-user/list`,
      /** 注销审核 */
      deletions: `${ROOTS.HQ}/app-user/deletions`,
      /** 用户详情 */
      detail: (id: string) => `${ROOTS.HQ}/app-user/${id}`,
    },
    /** 账号管理 */
    account: {
      /** 后台账号 */
      adminUsers: `${ROOTS.HQ}/account/admin-users`,
      /** 角色管理 */
      roles: `${ROOTS.HQ}/account/roles`,
      /** 权限管理 */
      permissions: `${ROOTS.HQ}/account/permissions`,
    },
    /** 系统管理 */
    system: {
      /** 系统概览 */
      overview: `${ROOTS.HQ}/system/overview`,
      /** 审计日志 */
      auditLogs: `${ROOTS.HQ}/system/audit-logs`,
      /** 监控 */
      monitoring: `${ROOTS.HQ}/system/monitoring`,
      /** 系统配置 */
      configurations: `${ROOTS.HQ}/system/configurations`,
      /** 菜单管理 */
      menus: `${ROOTS.HQ}/system/menus`,

      /** 系统设置 */
      settings: `${ROOTS.HQ}/system/settings`,
    },
  },
  home: { root: '/' },
  error: {
    unauthorized: `${ROOTS.ERROR}/401`,
    notFound: `${ROOTS.ERROR}/404`,
    forbidden: `${ROOTS.ERROR}/403`,
  },
};
